import React, { useEffect, useState } from "react";
import { Card, CardBody, CardHeader, Spinner, Row, Col } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import { APIClient } from "../../helpers/api_helper";

const api = new APIClient();

const AnalyticsChart = () => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await api.get("/api/admin/analytics");
      setAnalyticsData(response?.data || {});
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !analyticsData) {
    return (
      <Card>
        <CardBody className="text-center p-5">
          <Spinner color="primary" />
          <p className="mt-2">Loading analytics...</p>
        </CardBody>
      </Card>
    );
  }

  // --- Extract values ----
  const { monthlyOrders = [], monthlyIncome = [], dateYear } = analyticsData;

  // Month order logic → last 12 months ending at current month
  const monthOrder = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  const currentMonthIndex = new Date().getMonth(); // 0-11

  const rotatedMonths = [
    ...monthOrder.slice(currentMonthIndex + 1),
    ...monthOrder.slice(0, currentMonthIndex + 1)
  ];

  // Convert to map for fast lookup
  const orderMap = {};
  monthlyOrders.forEach(m => { orderMap[m.month] = m.count; });

  const incomeMap = {};
  monthlyIncome.forEach(m => { incomeMap[m.month] = m.income; });

  // Prepare final chart data (12 months)
  const bookingCounts = rotatedMonths.map(m => orderMap[m] || 0);
  const incomeCounts = rotatedMonths.map(m => incomeMap[m] || 0);

  // Function to calculate rounded y-axis values with custom margin percentage
  const calculateYAxisTicks = (dataArray, marginPercentage = 0.5, isCurrency = false) => {
    const maxValue = Math.max(...dataArray);
    const minValue = Math.min(...dataArray);

    if (maxValue === 0 && minValue === 0) {
      return {
        min: 0,
        max: 10,
        tickAmount: 5,
        step: 2
      };
    }

    // Calculate range and add custom margin percentage
    const range = maxValue - minValue;
    const margin = range * marginPercentage;
    const suggestedMin = Math.max(0, minValue - margin);
    const suggestedMax = maxValue + margin;

    // Determine appropriate step size based on the max value
    let step;
    if (suggestedMax <= 10) {
      step = 1;
    } else if (suggestedMax <= 50) {
      step = 5;
    } else if (suggestedMax <= 100) {
      step = 10;
    } else if (suggestedMax <= 500) {
      step = 50;
    } else if (suggestedMax <= 1000) {
      step = 100;
    } else if (suggestedMax <= 5000) {
      step = 500;
    } else if (suggestedMax <= 10000) {
      step = 1000;
    } else {
      step = Math.ceil(suggestedMax / 10 / 1000) * 1000;
    }

    // Round min and max to nearest step
    const actualMin = Math.floor(suggestedMin / step) * step;
    const actualMax = Math.ceil(suggestedMax / step) * step;

    // Calculate tick amount (between 5-10 ticks)
    const tickAmount = Math.min(10, Math.max(5, Math.round((actualMax - actualMin) / step)));

    return {
      min: actualMin,
      max: actualMax,
      tickAmount,
      step
    };
  };

  // Bookings chart: 50% margin
  const bookingsAxis = calculateYAxisTicks(bookingCounts, 0.5, false);

  // Income chart: 25% margin only
  const incomeAxis = calculateYAxisTicks(incomeCounts, 0.25, true);

  // Bookings Chart Options
  const bookingsChartOptions = {
    chart: {
      type: "line",
      height: 350,
      toolbar: {
        show: false // Remove toolbar
      }
    },
    grid: {
      borderColor: '#f1f1f1',
      strokeDashArray: 0,
    },
    stroke: {
      curve: "smooth",
      width: 3
    },
    colors: ["#556ee6"],
    xaxis: {
      categories: rotatedMonths,
      title: {
        text: "Months",
        style: {
          fontSize: '14px',
          fontWeight: 'bold'
        }
      },
      labels: {
        style: {
          fontSize: '12px'
        }
      }
    },
    yaxis: {
      title: {
        text: "Number of Bookings",
        style: {
          fontSize: '14px',
          fontWeight: 'bold'
        }
      },
      min: bookingsAxis.min,
      max: bookingsAxis.max,
      tickAmount: bookingsAxis.tickAmount,
      labels: {
        formatter: function (val) {
          return Math.round(val);
        },
        style: {
          fontSize: '12px'
        }
      }
    },
    markers: {
      size: 5,
      hover: {
        size: 7
      }
    },
    tooltip: {
      y: {
        formatter: function (val) {
          return Math.round(val) + " bookings";
        }
      }
    },
    legend: {
      show: false
    },
    title: {
      text: "Monthly Bookings",
      align: 'left',
      style: {
        fontSize: '16px',
        fontWeight: 'bold',
        color: '#343a40'
      }
    },
    annotations: {
      yaxis: [{
        y: Math.max(...bookingCounts),
        borderColor: '#556ee6',
        label: {
          borderColor: '#556ee6',
          style: {
            color: '#fff',
            background: '#556ee6',
          },
          text: `Max: ${Math.max(...bookingCounts)}`,
          position: 'left'
        }
      }]
    }
  };

  // Income Chart Options
  const incomeChartOptions = {
    chart: {
      type: "area",
      height: 350,
      toolbar: {
        show: false // Remove toolbar
      }
    },
    grid: {
      borderColor: '#f1f1f1',
      strokeDashArray: 0,
    },
    stroke: {
      curve: "smooth",
      width: 3
    },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.6,
        opacityTo: 0.1,
        stops: [0, 90, 100]
      }
    },
    colors: ["#34c38f"],
    xaxis: {
      categories: rotatedMonths,
      title: {
        text: "Months",
        style: {
          fontSize: '14px',
          fontWeight: 'bold'
        }
      },
      labels: {
        style: {
          fontSize: '12px'
        }
      }
    },
    yaxis: {
      title: {
        text: "Income (₹)",
        style: {
          fontSize: '14px',
          fontWeight: 'bold'
        }
      },
      min: incomeAxis.min,
      max: incomeAxis.max,
      tickAmount: incomeAxis.tickAmount,
      labels: {
        formatter: function (val) {
          return "₹" + Math.round(val).toLocaleString('en-IN');
        },
        style: {
          fontSize: '12px'
        }
      }
    },
    markers: {
      size: 5,
      hover: {
        size: 7
      }
    },
    tooltip: {
      y: {
        formatter: function (val) {
          return "₹" + Math.round(val).toLocaleString('en-IN');
        }
      }
    },
    legend: {
      show: false
    },
    title: {
      text: "Monthly Income",
      align: 'left',
      style: {
        fontSize: '16px',
        fontWeight: 'bold',
        color: '#343a40'
      }
    },
    annotations: {
      yaxis: [{
        y: Math.max(...incomeCounts),
        borderColor: '#34c38f',
        label: {
          borderColor: '#34c38f',
          style: {
            color: '#fff',
            background: '#34c38f',
          },
          text: `Max: ₹${Math.max(...incomeCounts).toLocaleString('en-IN')}`,
          position: 'left'
        }
      }]
    }
  };

  const bookingsSeries = [
    {
      name: "Bookings",
      data: bookingCounts
    }
  ];

  const incomeSeries = [
    {
      name: "Income",
      data: incomeCounts
    }
  ];

  return (
    <Card className="analytics-chart-card" style={{ marginBottom: '20px', borderRedius: '10px' }}>
      <CardHeader>
        <h5 className="mb-0">Analytics Dashboard ({dateYear})</h5>
      </CardHeader>
      <CardBody className="p-3">
        <Row>
          {/* Bookings Chart - Full width */}
          <Col xs={12} className="mb-4">
            <div className="chart-wrapper">
              <ReactApexChart
                options={bookingsChartOptions}
                series={bookingsSeries}
                type="line"
                height={350}
              />
            </div>
          </Col>

          {/* Income Chart - Full width */}
          <Col xs={12}>
            <div className="chart-wrapper">
              <ReactApexChart
                options={incomeChartOptions}
                series={incomeSeries}
                type="area"
                height={350}
              />
            </div>
          </Col>
        </Row>
      </CardBody>

      <style jsx>{`
        .analytics-chart-card {
          width: 100%;
          margin: 0 auto;
        }
        
        .chart-wrapper {
          width: 100%;
          border: 1px solid #e9ecef;
          border-radius: 8px;
          padding: 20px;
          background-color: white;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
          position: relative;
        }
        
        .chart-title-with-margin {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 10px;
          padding-bottom: 10px;
          border-bottom: 1px dashed #e9ecef;
        }
        
        .margin-label {
          font-size: 12px;
          color: #6c757d;
          background: #f8f9fa;
          padding: 2px 8px;
          border-radius: 4px;
          border: 1px solid #dee2e6;
        }
        
        /* Remove any chart toolbar */
        :global(.apexcharts-toolbar) {
          display: none !important;
        }
        
        /* Make charts responsive */
        :global(.apexcharts-canvas) {
          width: 100% !important;
        }
        
        @media (max-width: 768px) {
          .chart-wrapper {
            padding: 15px;
          }
          
          .margin-label {
            font-size: 11px;
          }
          
          :global(.apexcharts-canvas) {
            min-width: 100% !important;
          }
        }
      `}</style>
    </Card>
  );
};

export default AnalyticsChart;